import { Link } from 'gatsby'
import { GatsbyImage, type IGatsbyImageData } from 'gatsby-plugin-image'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { Marquee } from 'src/components/atoms/marquee'
import { EventCountdown } from 'src/components/molecules/eventCountdown'
import { PrismicLinkType } from 'src/typings/generated/graphql'
import { generateRandomId } from 'src/utils/domHelper'
import { useMediaQuery } from 'src/utils/useMediaQuery'
import * as Styles from './countdownBanner.module.scss'

/**
 * Central value for an image focus point axis, used when no focus point is provided for an axis of a background image.
 */
const CENTERED_FOCUS = 0.5

/**
 * Type guard to check if a given date is a valid `Date` object.
 */
const isValidDate = (date: Date | undefined): date is Date => {
  return date instanceof Date && !Number.isNaN(date.getTime())
}

/**
 * Function that recalculates the number of seconds until `eventDate` from the current time.
 *
 * It returns `0` if `eventDate` has already passed.
 */
const calculateSecondsUntilEvent = (eventDate: Date): number => {
  const eventDateEpoch = eventDate.getTime()
  const currentDateEpoch = new Date().getTime()

  const differenceInSeconds = Math.floor(
    (eventDateEpoch - currentDateEpoch) / 1000
  )

  // Clamp to zero seconds for events that have already passed.
  return Math.max(differenceInSeconds, 0)
}

/**
 * Image data for a background image, including the Gatsby image data and an optional alternative text.
 */
type ImageData = {
  imageData?: IGatsbyImageData
  alt?: string
}

/**
 * Focus point data for a background image, used to determine which part of the image should be always visible.
 */
type ImageFocusData = {
  focusX?: number
  focusY?: number
}

/**
 * Contents of the props for the `CountdownBanner` component.
 */
export type CountdownBannerContent = {
  anchor?: string
  eventDate?: Date
  headlineBeforeEvent?: string
  backgroundImage?: ImageData & ImageFocusData
  headlineAfterEvent?: string
  description?: string
  ctaLink?: PrismicLinkType
  ctaText?: string
  ctaIcon?: ImageData
}

/**
 * Props for the `CountdownBanner` component.
 */
type CountdownBannerProps = {
  content: CountdownBannerContent
}

/**
 * Banner that displays a countdown to a specified event date and updates its content based on whether the event has
 * already occurred.
 */
const CountdownBanner: FC<CountdownBannerProps> = ({ content }) => {
  const {
    anchor = generateRandomId(),
    eventDate,
    headlineBeforeEvent,
    backgroundImage,
    headlineAfterEvent,
    description,
    ctaLink,
    ctaText,
    ctaIcon,
  } = content

  const [hasMounted, setHasMounted] = useState(false)
  const [secondsUntilEvent, setSecondsUntilEvent] = useState<number>(0)
  const isDesktop = useMediaQuery('(min-width: 48em)')

  const isBeforeEvent = useMemo(() => {
    return isValidDate(eventDate) && secondsUntilEvent > 0
  }, [eventDate, secondsUntilEvent])

  const isAfterEvent = useMemo(() => {
    return isValidDate(eventDate) && secondsUntilEvent === 0
  }, [eventDate, secondsUntilEvent])

  useEffect(() => {
    setHasMounted(true)
  }, [])

  useEffect(() => {
    // Skip countdown update interval setup if the event date is invalid
    if (!isValidDate(eventDate)) {
      return undefined
    }

    // Set the initial countdown value based on the current time and the event date
    setSecondsUntilEvent(calculateSecondsUntilEvent(eventDate))

    // Update the countdown value every second until the event date is reached
    const interval = setInterval(() => {
      const newSecondsUntilEvent = calculateSecondsUntilEvent(eventDate)
      setSecondsUntilEvent(newSecondsUntilEvent)

      if (newSecondsUntilEvent === 0) {
        clearInterval(interval)
      }
    }, 1000)

    // Clear the countdown update interval when the component unmounts or when eventDate changes
    return () => {
      clearInterval(interval)
    }
  }, [eventDate])

  /**
   * Memoized element that displays the background image after the event has occurred.
   */
  const backgroundElement = useMemo(() => {
    return (
      <div className={Styles.backgroundWrapper}>
        {isAfterEvent && backgroundImage?.imageData && (
          <GatsbyImage
            image={backgroundImage.imageData}
            alt={backgroundImage.alt ?? ''}
            className={Styles.backgroundImageWrapper}
            imgClassName={Styles.backgroundImage}
            style={
              {
                '--background-image-focus-x':
                  backgroundImage.focusX ?? CENTERED_FOCUS,
                '--background-image-focus-y':
                  backgroundImage.focusY ?? CENTERED_FOCUS,
              } as React.CSSProperties
            }
          />
        )}
      </div>
    )
  }, [isAfterEvent, backgroundImage])

  /**
   * Memoized element that displays the headline and countdown before the event occurs.
   */
  const contentBeforeEventElement = useMemo(() => {
    return (
      <div className={`${Styles.content} ${Styles.beforeEvent}`}>
        <span className={Styles.textBeforeEvent}>{headlineBeforeEvent}</span>
        <div className={Styles.countdownWrapper}>
          <EventCountdown secondsUntilEvent={secondsUntilEvent} />
        </div>
      </div>
    )
  }, [headlineBeforeEvent, secondsUntilEvent])

  /**
   * Memoized element that displays the headline and description after the event has occurred.
   *
   * Used both within a marquee in small screens or as a standalone text line in larger screens.
   */
  const textAfterEventElement = useMemo(() => {
    return (
      <span className={Styles.textAfterEvent}>
        <span className={Styles.headlineAfterEvent}>{headlineAfterEvent}</span>
        <span className={Styles.textAfterEventSeparator}>—</span>
        <span className={Styles.description}>{description}</span>
      </span>
    )
  }, [headlineAfterEvent, description])

  /**
   * Memoized element that displays the headline, description, and CTA link after the event has occurred.
   */
  const contentAfterEventElement = useMemo(() => {
    return (
      <div className={`${Styles.content} ${Styles.afterEvent}`}>
        {!isDesktop && <Marquee speed={32}>{textAfterEventElement}</Marquee>}

        {isDesktop && textAfterEventElement}

        <Link to={ctaLink?.url ?? '#'} className={Styles.ctaLink}>
          {ctaIcon?.imageData && (
            <GatsbyImage
              image={ctaIcon.imageData}
              alt={ctaIcon.alt ?? ''}
              className={Styles.ctaIcon}
            />
          )}
          {ctaText}
        </Link>
      </div>
    )
  }, [isDesktop, textAfterEventElement, ctaLink, ctaIcon, ctaText])

  return (
    <section id={anchor} className={Styles.countdownBanner}>
      {hasMounted && isValidDate(eventDate) && (
        <>
          {backgroundElement}

          {isBeforeEvent && contentBeforeEventElement}

          {isAfterEvent && contentAfterEventElement}
        </>
      )}
    </section>
  )
}

export default CountdownBanner
